import { Link, createFileRoute } from '@tanstack/react-router'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  getGetAdminTournamentQueryKey,
  getListAdminTournamentsQueryKey,
  useAddTournamentEntry,
  useGetAdminTournament,
  useRemoveTournamentEntry,
  useUpdateTournament,
  useUpdateTournamentEntry,
} from '../../../api/generated/endpoints/admin/admin'
import { useSearchCustomers } from '../../../api/generated/endpoints/customers/customers'
import type { AdminEntryRead, AdminTournamentRead } from '../../../api/generated/model'
import { apiMessage } from '../../../lib/api-error'
import { formatUYU } from '../../../lib/money'
import { formatDayLong } from '../../../lib/time'
import { handicapLabel } from '../../../lib/tournaments'
import { Field, INPUT, StatusPill } from './admin.tournaments'

/**
 * Running one tournament: who is in, who has paid, what they shot.
 *
 * The scores table is the screen the coach actually uses, on a Saturday, with
 * people waiting. So every row saves on its own — one button, one request, one
 * thing that can fail — rather than a single Save at the bottom that either
 * works entirely or leaves him guessing which row did not.
 */
export const Route = createFileRoute('/_authed/_admin/admin/tournament/$tournamentId')({
  component: ManageTournamentPage,
})

const NEXT_STATUS: Record<string, { to: string; label: string; hint: string }[]> = {
  draft: [
    { to: 'open', label: 'Abrir inscripciones', hint: 'Lo publica. Se puede ver y anotarse.' },
  ],
  open: [
    {
      to: 'closed',
      label: 'Cerrar inscripciones',
      hint: 'Sigue visible; no se puede anotar nadie más.',
    },
    { to: 'draft', label: 'Volver a borrador', hint: 'Lo vuelve a ocultar.' },
  ],
  closed: [
    { to: 'completed', label: 'Marcar finalizado', hint: 'Las posiciones quedan definitivas.' },
    { to: 'open', label: 'Reabrir inscripciones', hint: 'Vuelve a permitir inscripciones.' },
  ],
  completed: [
    {
      to: 'closed',
      label: 'Reabrir la carga de tarjetas',
      hint: 'Por si hay que corregir una tarjeta.',
    },
  ],
}

function ManageTournamentPage() {
  const { tournamentId } = Route.useParams()
  const tournament = useGetAdminTournament(tournamentId)

  if (tournament.isPending) return <p className="text-slate-600">Cargando…</p>
  if (tournament.isError) {
    return (
      <div className="space-y-4">
        <p role="alert" className="text-red-700">
          No se pudo cargar ese torneo.
        </p>
        <Link to="/admin/tournaments" className="text-xl underline">
          Volver a los torneos
        </Link>
      </div>
    )
  }

  return <Manage tournament={tournament.data} />
}

function Manage({ tournament }: { tournament: AdminTournamentRead }) {
  const queryClient = useQueryClient()
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetAdminTournamentQueryKey(tournament.id) }),
      queryClient.invalidateQueries({ queryKey: getListAdminTournamentsQueryKey() }),
    ])

  const update = useUpdateTournament({ mutation: { retry: false, onSuccess: refresh } })

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/admin/tournaments"
          className="inline-flex min-h-12 items-center text-base underline"
        >
          ← Todos los torneos
        </Link>
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="text-3xl font-semibold">{tournament.name}</h1>
          <StatusPill status={tournament.status} />
        </div>
        <p className="mt-1 text-slate-700">
          {formatDayLong(tournament.event_date)}
          {tournament.gspro_course && <> &middot; {tournament.gspro_course}</>} ·{' '}
          {tournament.entry_fee_uyu_pesos > 0
            ? formatUYU(tournament.entry_fee_uyu_pesos)
            : 'inscripción libre'}
        </p>
        {tournament.status !== 'draft' && (
          <p className="mt-2 text-base">
            Tabla pública:{' '}
            <a
              href={`/t/${tournament.slug}`}
              className="inline-flex min-h-12 items-center underline"
              target="_blank"
              rel="noreferrer"
            >
              /t/{tournament.slug}
            </a>{' '}
            <span className="text-slate-600">— cualquiera con este enlace puede abrirlo.</span>
          </p>
        )}
      </div>

      <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
        <h2 className="text-2xl font-semibold">En qué etapa está</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          {(NEXT_STATUS[tournament.status] ?? []).map((option) => (
            <button
              key={option.to}
              type="button"
              onClick={() =>
                update.mutate({ tournamentId: tournament.id, data: { status: option.to } })
              }
              disabled={update.isPending}
              className="min-h-16 max-w-xs rounded-md border-2 border-slate-400 px-6 text-left disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              <span className="block text-xl font-medium">{option.label}</span>
              <span className="block text-sm text-slate-600">{option.hint}</span>
            </button>
          ))}
        </div>
        {tournament.status === 'draft' && (
          <p className="mt-4 rounded-md bg-slate-100 p-4 text-base text-slate-700">
            Bloquear los simuladores para este torneo en el{' '}
            <Link
              to="/admin/calendar"
              search={{ date: tournament.event_date }}
              className="underline"
            >
              calendario
            </Link>
            . Un torneo no reserva horas de simulador por sí solo.
          </p>
        )}
        {update.isError && (
          <p role="alert" className="mt-3 text-red-700">
            {apiMessage(update.error, 'El cambio no se aplicó.')}
          </p>
        )}
      </section>

      <Roster tournament={tournament} onChanged={refresh} />
      <Details tournament={tournament} />
    </div>
  )
}

function Roster({
  tournament,
  onChanged,
}: {
  tournament: AdminTournamentRead
  onChanged: () => Promise<unknown>
}) {
  return (
    <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
      <h2 className="text-2xl font-semibold">
        Jugadores{' '}
        <span className="text-lg font-normal text-slate-600">
          ({tournament.entrants}
          {tournament.max_players !== null && ` de ${tournament.max_players}`})
        </span>
      </h2>

      {tournament.entries.length === 0 ? (
        <p className="mt-4 text-slate-600">Todavía no hay inscriptos.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {tournament.entries.map((entry) => (
            <EntryRow key={entry.entry_id} entry={entry} onChanged={onChanged} />
          ))}
        </ul>
      )}

      <AddEntrant tournament={tournament} />
    </section>
  )
}

function EntryRow({
  entry,
  onChanged,
}: {
  entry: AdminEntryRead
  onChanged: () => Promise<unknown>
}) {
  const [gross, setGross] = useState(entry.gross_strokes?.toString() ?? '')
  const [handicap, setHandicap] = useState(entry.handicap.toString())

  // Re-sync when the server sends different numbers back, e.g. after another
  // row's save refetched the whole tournament.
  useEffect(() => {
    setGross(entry.gross_strokes?.toString() ?? '')
    setHandicap(entry.handicap.toString())
  }, [entry.gross_strokes, entry.handicap])

  const save = useUpdateTournamentEntry({ mutation: { retry: false, onSuccess: onChanged } })
  const remove = useRemoveTournamentEntry({ mutation: { retry: false, onSuccess: onChanged } })

  const dirty =
    gross !== (entry.gross_strokes?.toString() ?? '') || handicap !== entry.handicap.toString()

  return (
    <li className="rounded-md border-2 border-slate-200 p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="w-10 text-xl font-semibold tabular-nums text-slate-500">
          {entry.position ?? '–'}
        </span>
        <span className="text-xl font-medium">{entry.name ?? 'Unnamed'}</span>
        {entry.phone && <span className="text-base text-slate-600">{entry.phone}</span>}
        <span className="ml-auto text-base">
          {entry.paid ? (
            <span className="text-green-800">Pago</span>
          ) : (
            <button
              type="button"
              onClick={() => save.mutate({ entryId: entry.entry_id, data: { paid: true } })}
              className="min-h-12 rounded-md border-2 border-amber-500 bg-amber-50 px-4 font-medium text-amber-900"
            >
              Marcar pago
            </button>
          )}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Bruto</span>
          <input
            type="number"
            min={1}
            inputMode="numeric"
            value={gross}
            onChange={(event) => setGross(event.target.value)}
            className="mt-1 min-h-14 w-28 rounded-md border-2 border-slate-400 px-3 text-xl tabular-nums"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Hándicap</span>
          <input
            type="number"
            inputMode="numeric"
            value={handicap}
            onChange={(event) => setHandicap(event.target.value)}
            className="mt-1 min-h-14 w-28 rounded-md border-2 border-slate-400 px-3 text-xl tabular-nums"
          />
        </label>
        <p className="min-h-14 pb-2 text-xl">
          <span className="text-sm text-slate-600">Neto </span>
          <span className="font-semibold tabular-nums">
            {gross.trim() === '' ? '–' : Number(gross) - Number(handicap || 0)}
          </span>
          <span className="ml-2 text-sm text-slate-500">
            ({handicapLabel(Number(handicap || 0))})
          </span>
        </p>

        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() =>
            save.mutate({
              entryId: entry.entry_id,
              data:
                gross.trim() === ''
                  ? { clear_score: true, handicap: Number(handicap || 0) }
                  : { gross_strokes: Number(gross), handicap: Number(handicap || 0) },
            })
          }
          className="min-h-14 rounded-md bg-slate-900 px-6 text-lg text-white disabled:opacity-40"
        >
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </button>

        <button
          type="button"
          onClick={() => {
            if (window.confirm(`¿Quitar a ${entry.name ?? 'este jugador'} del torneo?`)) {
              remove.mutate({ entryId: entry.entry_id })
            }
          }}
          className="ml-auto min-h-14 rounded-md px-4 text-base text-slate-600 underline"
        >
          Quitar
        </button>
      </div>

      {(save.isError || remove.isError) && (
        <p role="alert" className="mt-2 text-red-700">
          {apiMessage(save.error ?? remove.error, 'No se guardó.')}
        </p>
      )}
    </li>
  )
}

function AddEntrant({ tournament }: { tournament: AdminTournamentRead }) {
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 300)
    return () => clearTimeout(timer)
  }, [typed])

  const results = useSearchCustomers(
    { q: query },
    { query: { placeholderData: keepPreviousData, staleTime: 30_000, retry: 1 } },
  )

  const add = useAddTournamentEntry({
    mutation: {
      retry: false,
      onSuccess: async () => {
        setTyped('')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetAdminTournamentQueryKey(tournament.id) }),
          queryClient.invalidateQueries({ queryKey: getListAdminTournamentsQueryKey() }),
        ])
      },
    },
  })

  const already = new Set(tournament.entries.map((entry) => entry.user_id))
  const candidates = (results.data?.customers ?? []).filter((customer) => !already.has(customer.id))

  return (
    <div className="mt-6 border-t-2 border-slate-200 pt-6">
      <Field
        label="Agregar a alguien"
        hint="Para quien se anotó en el mostrador. También funciona en un borrador."
      >
        <input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder="Buscar por nombre o teléfono"
          className={INPUT}
        />
      </Field>

      {query.length > 0 && candidates.length === 0 && !results.isFetching && (
        <p className="mt-3 text-base text-slate-600">
          No se encontró a nadie. Hace falta que tenga cuenta antes de inscribirlo.
        </p>
      )}

      {/* In normal document flow, not a popover: on an iPad a floating list
          renders under the software keyboard. Same reasoning as StudentPicker. */}
      <ul className="mt-3 space-y-2">
        {candidates.map((customer) => (
          <li key={customer.id}>
            <button
              type="button"
              disabled={add.isPending}
              onClick={() =>
                add.mutate({
                  tournamentId: tournament.id,
                  data: { user_id: customer.id, paid: true, handicap: null },
                })
              }
              className="min-h-16 w-full rounded-md border-2 border-slate-300 px-5 text-left text-xl hover:bg-slate-50 disabled:opacity-50"
            >
              {customer.name ?? 'Unnamed'}
              {customer.phone && (
                <span className="ml-3 text-base text-slate-600">{customer.phone}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {add.isError && (
        <p role="alert" className="mt-3 text-red-700">
          {apiMessage(add.error, 'No se pudo agregar a esa persona.')}
        </p>
      )}
    </div>
  )
}

function Details({ tournament }: { tournament: AdminTournamentRead }) {
  const queryClient = useQueryClient()
  const [course, setCourse] = useState(tournament.gspro_course ?? '')
  const [notes, setNotes] = useState(tournament.notes ?? '')
  const [maxPlayers, setMaxPlayers] = useState(tournament.max_players?.toString() ?? '')
  const [playsFrom, setPlaysFrom] = useState(tournament.plays_from)
  const [playsUntil, setPlaysUntil] = useState(tournament.plays_until)
  const [roundMinutes, setRoundMinutes] = useState(String(tournament.round_minutes))

  const update = useUpdateTournament({
    mutation: {
      retry: false,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetAdminTournamentQueryKey(tournament.id) }),
          queryClient.invalidateQueries({ queryKey: getListAdminTournamentsQueryKey() }),
        ]),
    },
  })

  return (
    <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
      <h2 className="text-2xl font-semibold">Datos</h2>
      <form
        className="mt-4 space-y-5"
        onSubmit={(event) => {
          event.preventDefault()
          update.mutate({
            tournamentId: tournament.id,
            data: {
              gspro_course: course.trim() || null,
              notes: notes.trim() || null,
              max_players: maxPlayers.trim() === '' ? null : Number(maxPlayers),
              plays_from: playsFrom,
              plays_until: playsUntil,
              round_minutes: Number(roundMinutes),
            },
          })
        }}
      >
        <Field label="Cancha de GSPro">
          <input
            value={course}
            onChange={(event) => setCourse(event.target.value)}
            maxLength={200}
            className={INPUT}
          />
        </Field>
        <Field label="Máximo de jugadores" hint="En blanco, sin límite.">
          <input
            type="number"
            min={1}
            value={maxPlayers}
            onChange={(event) => setMaxPlayers(event.target.value)}
            className={INPUT}
          />
        </Field>
        {/* Editable after the fact, because a tournament that fills up early
            or runs long is exactly when the period needs moving. Changing the
            round length re-times only rounds booked FROM NOW ON -- the ones
            already booked keep the hours they were given. */}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Se puede jugar desde">
            <input
              type="date"
              value={playsFrom}
              onChange={(event) => setPlaysFrom(event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              value={playsUntil}
              onChange={(event) => setPlaysUntil(event.target.value)}
              className={INPUT}
            />
          </Field>
        </div>
        <Field
          label="Duración de una ronda"
          hint="En minutos. Los horarios ya reservados no se mueven."
        >
          <input
            type="number"
            min={10}
            step={10}
            value={roundMinutes}
            onChange={(event) => setRoundMinutes(event.target.value)}
            className={INPUT}
          />
        </Field>
        <Field label="Notas" hint="Se muestra en la página pública.">
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            className="min-h-24 w-full rounded-md border-2 border-slate-400 p-3 text-lg"
          />
        </Field>

        {update.isError && (
          <p role="alert" className="text-red-700">
            {apiMessage(update.error, 'We could not save that.')}
          </p>
        )}

        <button
          type="submit"
          disabled={update.isPending}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white disabled:opacity-50"
        >
          {update.isPending ? 'Guardando…' : 'Guardar los datos'}
        </button>
      </form>
    </section>
  )
}
